import type { ReactNode } from 'react';
import { Info } from 'lucide-react';
import { Container, PageHeader, Section, Toolbar } from '@vnetwork/ui/layout';
import { Alert } from '@vnetwork/ui/feedback';
import { KpiTiles, Stack, type Kpi } from '../_shared/AnalyticsKit';

export interface BusinessUsageShellProps {
  /** Per-service header icon (Gauge / HardDrive / Receipt …). */
  icon: ReactNode;
  title?: string;
  description: string;
  breadcrumbs?: ReactNode;
  /** Alert(info) body — the billing-basis note (always shown). */
  basis: ReactNode;
  /** Optional extra alert below the basis note (e.g. an error banner). */
  extraAlert?: ReactNode;
  /** Toolbar start — period selector (+ optional scope filter). */
  period: ReactNode;
  /** Toolbar end — grouping / unit / export controls. */
  actions?: ReactNode;
  kpis: Kpi[];
  /** KPI column count; defaults to the number of tiles. */
  kpiColumns?: number;
  kpisLoading?: boolean;
  /** Section title — the selected period label. */
  periodLabel: string;
  /** Section actions — the breakdown filter input. */
  search?: ReactNode;
  /** The breakdown: flat table / grouped cards / region split. */
  children: ReactNode;
}

/**
 * Shared chrome for every service's Business Usage page:
 *   Container → PageHeader → Alert(basis) → Toolbar(period | actions) → KpiTiles
 *   → Section(periodLabel | search) → breakdown.
 *
 * Owns layout + the KPI strip only. Each page fetches its own data and supplies the
 * KPIs + breakdown, so per-service differences (CDN grouping/distribution, OSS bucket
 * table, WAAP sortable+pagination) live entirely in `children` — not here. Adding a
 * new service's usage view = write its adapter (KPIs + breakdown), reuse this shell.
 */
export default function BusinessUsageShell({
  icon,
  title = 'Business Usage',
  description,
  breadcrumbs,
  basis,
  extraAlert,
  period,
  actions,
  kpis,
  kpiColumns,
  kpisLoading,
  periodLabel,
  search,
  children,
}: BusinessUsageShellProps) {
  return (
    <Container size="xl">
      <PageHeader icon={icon} breadcrumbs={breadcrumbs} title={title} description={description} />
      <Stack>
        <Alert tone="info" icon={<Info size={16} />}>
          {basis}
        </Alert>
        {extraAlert}
        <Toolbar start={period} end={actions} />
        {/* Empty KPI array → render nothing (a page may compute no tiles for the
            current mode/period); loading state is owned by KpiTiles when tiles exist. */}
        {kpis.length > 0 && (
          <KpiTiles items={kpis} columns={kpiColumns ?? kpis.length} loading={kpisLoading} />
        )}
        <Section style={{ marginBottom: 0 }} title={periodLabel} actions={search}>
          {children}
        </Section>
      </Stack>
    </Container>
  );
}
